import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { ChatTimelineItem, JourneyPlanItem } from "@/lib/chat-timeline";
import type { WecomInboundEvent } from "@/lib/wecom-inbound-events";
import {
  inboundAddClearedChurn,
  labelWecomInboundEvent,
} from "@/lib/wecom-inbound-event-labels";
import { Button } from "@/components/ui/button";
import { CalendarClock, ChevronDown, ChevronRight, Loader2, Send } from "lucide-react";
import { planDraftActions } from "@/lib/plan-draft-actions";
import { planCardTitle } from "@/lib/timeline-zones";
import { formatJourneyTouchDisplay } from "@/lib/journey-touch-display";
import type { WecomOutboundMessage } from "@/lib/wecom-outbound-messages";

function formatEventTime(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function inboundInitials(event: WecomInboundEvent): string {
  const src =
    event.staff_userid ||
    event.from_username ||
    event.change_type ||
    event.event_type ||
    event.msg_type ||
    "?";
  return src.slice(0, 2).toUpperCase();
}

/** 可读摘要：文本消息正文，或关系变更的跟进人；编辑资料附带字段 diff */
function inboundSummary(event: WecomInboundEvent): string | null {
  const text = event.content_text?.trim();
  if (event.staff_userid) {
    if (event.change_type === "del_follow_user") {
      return `客户删除了跟进人 ${event.staff_userid}`;
    }
    if (event.change_type === "del_external_contact") {
      return `员工 ${event.staff_userid} 删除了客户`;
    }
    if (event.change_type === "add_external_contact") {
      return `跟进人 ${event.staff_userid} 添加了客户`;
    }
    if (event.change_type === "edit_external_contact") {
      const head = `跟进人 ${event.staff_userid} 更新了客户资料`;
      return text ? `${head}\n${text}` : head;
    }
    if (text) return text;
    return `跟进人 ${event.staff_userid}`;
  }
  return text || null;
}

/** 任务区：虚线次要；长文可折叠 */
function TouchTaskBlock({ text }: { text: string }) {
  const long = text.length > 72 || text.includes("\n");
  const [open, setOpen] = useState(!long);

  if (!long) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2">
        <p className="text-[11px] font-medium text-muted-foreground">任务</p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{text}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2">
      <button
        type="button"
        className="flex w-full items-center gap-1 text-left text-[11px] font-medium text-muted-foreground"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="size-3 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="size-3 shrink-0" aria-hidden />
        )}
        任务
        {!open ? (
          <span className="ml-1 truncate font-normal text-muted-foreground/80">
            {text.replace(/\s+/g, " ").slice(0, 36)}
            {text.length > 36 ? "…" : ""}
          </span>
        ) : null}
      </button>
      {open ? (
        <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{text}</p>
      ) : null}
    </div>
  );
}

/** 消息区：实线主卡片；占位用 muted；成稿中脉冲「正在生成」 */
function TouchMessageBlock({
  text,
  isPlaceholder,
  tone = "outbound",
  generating = false,
}: {
  text: string;
  isPlaceholder: boolean;
  tone?: "outbound" | "plan";
  generating?: boolean;
}) {
  if (generating) {
    return (
      <div
        className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
        aria-busy="true"
        aria-live="polite"
      >
        <p className="text-[11px] font-medium text-muted-foreground">消息</p>
        <div className="mt-2 flex items-center gap-2">
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" aria-hidden />
          <p className="text-sm font-medium text-foreground">正在生成</p>
        </div>
        <div className="mt-2 space-y-1.5" aria-hidden>
          <div className="h-2.5 w-[92%] animate-pulse rounded bg-muted/80" />
          <div className="h-2.5 w-[70%] animate-pulse rounded bg-muted/60" />
          <div className="h-2.5 w-[48%] animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2",
        isPlaceholder
          ? "border-dashed border-border bg-muted/15"
          : tone === "outbound"
            ? "border-border bg-primary/5"
            : "border-border bg-muted/30",
      )}
    >
      <p className="text-[11px] font-medium text-muted-foreground">消息</p>
      <p
        className={cn(
          "mt-1 whitespace-pre-wrap text-sm",
          isPlaceholder ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {text}
      </p>
    </div>
  );
}

type InboundEventTimelineProps = {
  items: ChatTimelineItem[];
  className?: string;
  /** 当前联系人 churned_at；用于「已标记流失」启发式 */
  contactChurnedAt?: string | null;
  /** 计划步「立即发送」→ 企微待确认 */
  onSendPlanNow?: (plan: JourneyPlanItem) => void;
  sendingPlanStepId?: number | null;
  onDraftPlan?: (plan: JourneyPlanItem) => void;
  onRedraftPlan?: (plan: JourneyPlanItem) => void;
  /** 仅匹配当前 plan_step_id 的成稿中 */
  draftingPlanStepId?: number | null;
  /** 同客户已有其它步在成稿时禁用成稿按钮 */
  contactDraftBusy?: boolean;
  onCancelSend?: (message: WecomOutboundMessage) => void;
  onReschedule?: (source: { planStepId: number; outbound?: WecomOutboundMessage }) => void;
  onReplay?: (source: { planStepId: number; outbound?: WecomOutboundMessage }) => void;
  onOpenGroupPeers?: (message: WecomOutboundMessage) => void;
  cancellingOutboundId?: number | null;
  /** 接下来区：手册序最小且未在途的活步 id */
  nextStepId?: number | null;
};

/** Activity Feed 风格时间线：入站回调 + 临时外发 + 未来旅程计划（旧上新下） */
export function InboundEventTimeline({
  items,
  className,
  contactChurnedAt = null,
  onSendPlanNow,
  sendingPlanStepId = null,
  onDraftPlan,
  onRedraftPlan,
  draftingPlanStepId = null,
  contactDraftBusy = false,
  onCancelSend,
  onReschedule,
  onReplay,
  onOpenGroupPeers,
  cancellingOutboundId = null,
  nextStepId = null,
}: InboundEventTimelineProps) {
  if (items.length === 0) return null;
  const inboundEvents = items
    .filter((i): i is Extract<ChatTimelineItem, { kind: "inbound" }> => i.kind === "inbound")
    .map((i) => i.event);

  return (
    <ol className={cn("relative space-y-0", className)}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        if (item.kind === "inbound") {
          return (
            <InboundRow
              key={`in-${item.event.id}`}
              event={item.event}
              isLast={isLast}
              contactChurnedAt={contactChurnedAt}
              inboundEvents={inboundEvents}
            />
          );
        }
        if (item.kind === "outbound") {
          return (
            <OutboundRow
              key={`out-${item.message.id}`}
              message={item.message}
              when={formatEventTime(item.message.created_at)}
              isLast={isLast}
              onCancelSend={onCancelSend}
              onReschedule={onReschedule}
              onReplay={onReplay}
              onOpenGroupPeers={onOpenGroupPeers}
              cancelling={cancellingOutboundId === item.message.id}
            />
          );
        }
        const planStepId = item.plan.planStepId;
        return (
          <PlanRow
            key={item.plan.id}
            plan={item.plan}
            when={formatEventTime(item.plan.scheduledAt)}
            nextStepId={nextStepId}
            isLast={isLast}
            onSendNow={onSendPlanNow}
            sending={planStepId != null && planStepId === sendingPlanStepId}
            onDraft={onDraftPlan}
            onRedraft={onRedraftPlan}
            drafting={planStepId != null && planStepId === draftingPlanStepId}
            draftDisabled={contactDraftBusy && planStepId !== draftingPlanStepId}
          />
        );
      })}
    </ol>
  );
}

function TimelineRail({ isLast, children }: { isLast: boolean; children: ReactNode }) {
  return (
    <li className="relative flex gap-3 pb-6">
      {!isLast ? (
        <span
          aria-hidden
          className="absolute top-10 bottom-0 left-5 w-px -translate-x-1/2 bg-border"
        />
      ) : null}
      {children}
    </li>
  );
}

function InboundRow({
  event,
  isLast,
  contactChurnedAt,
  inboundEvents,
}: {
  event: WecomInboundEvent;
  isLast: boolean;
  contactChurnedAt: string | null;
  inboundEvents: WecomInboundEvent[];
}) {
  const [rawOpen, setRawOpen] = useState(false);
  const when = formatEventTime(event.wecom_created_at || event.created_at);
  const summary = inboundSummary(event);
  const change = event.change_type;
  const justMarkedChurned = change === "del_follow_user" && Boolean(contactChurnedAt);
  const clearedChurn = inboundAddClearedChurn(event, inboundEvents);
  const label = labelWecomInboundEvent(event, { justMarkedChurned, clearedChurn });
  const titleClass =
    label.tone === "warning"
      ? "text-amber-700 dark:text-amber-400"
      : label.tone === "recovery"
        ? "text-emerald-700 dark:text-emerald-400"
        : "text-foreground";
  const titleZh = label.titleZh || event.headline;
  const hasRaw = Boolean(event.raw_xml?.trim());

  return (
    <TimelineRail isLast={isLast}>
      <span className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-medium text-muted-foreground">
        {inboundInitials(event)}
      </span>
      <div className="min-w-0 flex-1 space-y-1.5 pt-1">
        <div>
          <p className={cn("text-sm font-medium leading-snug", titleClass)}>{titleZh}</p>
          {label.titleEn ? (
            <p className="font-mono text-xs text-muted-foreground">{label.titleEn}</p>
          ) : null}
          {when ? <p className="mt-0.5 text-xs text-muted-foreground">{when}</p> : null}
        </div>
        {summary ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{summary}</p>
        ) : null}
        {hasRaw ? (
          <div>
            <button
              type="button"
              className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              aria-expanded={rawOpen}
              onClick={() => setRawOpen((v) => !v)}
            >
              {rawOpen ? "收起 raw" : "raw"}
            </button>
            {rawOpen ? (
              <div className="mt-1.5 rounded-lg border border-border bg-muted/30 px-3 py-2">
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-foreground/90">
                  {event.raw_xml}
                </pre>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </TimelineRail>
  );
}

function OutboundRow({
  message,
  when,
  isLast,
  onCancelSend,
  onReschedule,
  onReplay,
  onOpenGroupPeers,
  cancelling,
}: {
  message: WecomOutboundMessage;
  when: string;
  isLast: boolean;
  onCancelSend?: (message: WecomOutboundMessage) => void;
  onReschedule?: (source: { planStepId: number; outbound?: WecomOutboundMessage }) => void;
  onReplay?: (source: { planStepId: number; outbound?: WecomOutboundMessage }) => void;
  onOpenGroupPeers?: (message: WecomOutboundMessage) => void;
  cancelling?: boolean;
}) {
  const { headline, content, status, error_message: errorMessage, journey_plan_step_id: planStepId } =
    message;
  const journeyLinked = planStepId != null || message.journey_customer_binding_id != null;
  const isVoided =
    status === "failed" &&
    ((errorMessage?.startsWith("已作废") ?? false) || headline.includes("已作废"));
  const isCancelled = status === "cancelled" || errorMessage?.startsWith("已取消");
  const peerCount = message.group_peer_count ?? 1;
  const showCancel = Boolean(onCancelSend) && status === "pending_confirm";
  const showReplay = Boolean(onReplay) && status === "sent" && planStepId != null;
  const showReschedule =
    Boolean(onReschedule) && planStepId != null && (isVoided || isCancelled || status === "failed");

  const touch = journeyLinked
    ? formatJourneyTouchDisplay({
        instruction: message.instruction,
        messageContent: content,
        needsAiDraft: false,
      })
    : null;

  return (
    <TimelineRail isLast={isLast}>
      <span className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
        <Send className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 space-y-2 pt-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{headline}</p>
            {when ? <p className="mt-0.5 text-xs text-muted-foreground">{when}</p> : null}
            {status === "failed" && errorMessage ? (
              <p
                className={
                  errorMessage.startsWith("已作废") || errorMessage.startsWith("已取消")
                    ? "mt-0.5 text-xs text-muted-foreground"
                    : "mt-0.5 text-xs text-destructive"
                }
              >
                {errorMessage}
              </p>
            ) : null}
            {status === "cancelled" && errorMessage ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{errorMessage}</p>
            ) : null}
            {status === "pending_confirm" ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                请到企业微信 → 群发助手确认后才会发给客户
                {peerCount > 1 && onOpenGroupPeers ? (
                  <>
                    {" · "}
                    <button
                      type="button"
                      className="underline-offset-2 hover:underline"
                      onClick={() => onOpenGroupPeers(message)}
                    >
                      群发 {peerCount} 人
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
            {status === "sent" ? (
              <p className="mt-0.5 text-xs text-muted-foreground">员工已在企微确认并发送</p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            {showCancel ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={cancelling}
                onClick={() => onCancelSend?.(message)}
              >
                {cancelling ? "取消中…" : "取消发送"}
              </Button>
            ) : null}
            {showReplay ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onReplay?.({ planStepId: planStepId!, outbound: message })}
              >
                再一次执行计划
              </Button>
            ) : null}
            {showReschedule ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onReschedule?.({ planStepId: planStepId!, outbound: message })}
              >
                再次安排
              </Button>
            ) : null}
          </div>
        </div>
        {touch ? (
          <div className="space-y-1.5">
            {touch.taskText ? <TouchTaskBlock text={touch.taskText} /> : null}
            {touch.showMessage ? (
              <TouchMessageBlock
                text={touch.messageText}
                isPlaceholder={touch.messageIsPlaceholder}
                tone="outbound"
              />
            ) : null}
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-primary/5 px-3 py-2">
            <p className="whitespace-pre-wrap text-sm text-foreground">{content}</p>
          </div>
        )}
      </div>
    </TimelineRail>
  );
}

function PlanRow({
  plan,
  when,
  nextStepId,
  isLast,
  onSendNow,
  sending,
  onDraft,
  onRedraft,
  drafting,
  draftDisabled = false,
}: {
  plan: JourneyPlanItem;
  when: string;
  nextStepId?: number | null;
  isLast: boolean;
  onSendNow?: (plan: JourneyPlanItem) => void;
  sending?: boolean;
  onDraft?: (plan: JourneyPlanItem) => void;
  onRedraft?: (plan: JourneyPlanItem) => void;
  drafting?: boolean;
  draftDisabled?: boolean;
}) {
  const {
    journeyName,
    body,
    instruction,
    offsetDays,
    preview,
    stepStatus,
    planStepId,
    needsAiDraft,
    handbookOrdinal,
  } = plan;
  const dayHint =
    offsetDays === 0 ? "今天" : offsetDays > 0 ? `第 ${offsetDays} 天` : `${Math.abs(offsetDays)} 天前`;
  const title = planCardTitle({
    planStepId: planStepId ?? -1,
    nextStepId: nextStepId ?? null,
    ordinal: handbookOrdinal ?? null,
  });
  const actions = planDraftActions({
    preview,
    needsAiDraft,
    planStepId,
    stepStatus,
  });
  const showDraft = actions.canDraftNow && Boolean(onDraft);
  const showRedraft = actions.canRedraft && Boolean(onRedraft);
  const showSendNow = actions.canSendNow && Boolean(onSendNow);

  const touch = formatJourneyTouchDisplay({
    instruction,
    messageContent: needsAiDraft ? null : body,
    needsAiDraft: Boolean(needsAiDraft),
  });

  return (
    <TimelineRail isLast={isLast}>
      <span className="relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border border-dashed border-border bg-background text-muted-foreground">
        <CalendarClock className="size-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 space-y-2 pt-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm leading-snug">
              <span className="font-medium text-foreground">{title}</span>
              <span className="text-muted-foreground"> · {journeyName}</span>
              {needsAiDraft ? (
                <span className="ml-1.5 inline-flex align-middle rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:text-amber-300">
                  占位稿·未经 AI
                </span>
              ) : null}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {when}
              {" · "}
              {dayHint}
              {preview ? (
                <span className="ml-1 text-muted-foreground/80">（预览）</span>
              ) : null}
            </p>
            {showDraft ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                点「立即成稿」马上跑编排，不必等三档钟点
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row">
            {showDraft ? (
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={drafting || draftDisabled}
                onClick={() => onDraft?.(plan)}
              >
                {drafting ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    成稿中…
                  </span>
                ) : (
                  "立即成稿"
                )}
              </Button>
            ) : null}
            {showRedraft ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={drafting || sending || draftDisabled}
                onClick={() => onRedraft?.(plan)}
              >
                {drafting ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                    成稿中…
                  </span>
                ) : (
                  "重新成稿"
                )}
              </Button>
            ) : null}
            {showSendNow ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={sending || drafting}
                onClick={() => onSendNow?.(plan)}
              >
                {sending ? "发送中…" : "立即发送"}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="space-y-1.5">
          {touch.taskText ? <TouchTaskBlock text={touch.taskText} /> : null}
          {touch.showMessage || drafting ? (
            <TouchMessageBlock
              text={touch.messageText}
              isPlaceholder={touch.messageIsPlaceholder}
              tone="plan"
              generating={drafting}
            />
          ) : null}
        </div>
      </div>
    </TimelineRail>
  );
}
